import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { shouldReduceMotion } from 'src/tef/components/config/animationConfig';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ForwardedHTMLProps } from 'src/tef/components/utils/types';

interface ModalBackdropProps extends ForwardedHTMLProps {
  isOpen: boolean;
  onClick?: () => void;
  opacity?: 'light' | 'medium' | 'dark';
  className?: string;
}

export const ModalBackdrop: React.FC<ModalBackdropProps> = ({
  isOpen,
  onClick,
  opacity = 'medium',
  className,
  ...props
}) => {
  const forwardedProps = forwardProps(props);
  const reduceMotion = shouldReduceMotion();

  const opacityClasses = {
    light: 'bg-black/30',
    medium: 'bg-black/50',
    dark: 'bg-black/70',
  };

  const backdropVariants = reduceMotion
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.15 } },
      };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={backdropVariants}
          className={cn('fixed inset-0 z-[100000]', opacityClasses[opacity], className)}
          onClick={onClick}
          {...forwardedProps}
          {...props}
        />
      )}
    </AnimatePresence>
  );
};

ModalBackdrop.displayName = 'ModalBackdrop';
